if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mediaVariantMap = this.getMediaVariantMap();
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.syncVariantSelectionFromMedia(event.detail.currentElement.dataset.mediaId);
      }

      setActiveMedia(mediaId, prepend, syncVariantSelection = !prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const headerOffset = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sh-header-offset')) || 0;
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already below the fixed header
          if (activeMediaRect.top > headerOffset + 10) return;
          const top = activeMediaRect.top + window.scrollY - headerOffset - 16;
          window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        if (syncVariantSelection) this.syncVariantSelectionFromMedia(mediaId);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail) return;
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      getMediaVariantMap() {
        const mapScript = this.querySelector('[data-media-variant-map]');
        if (!mapScript) return [];
        try {
          const parsed = JSON.parse(mapScript.textContent || '[]');
          return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          console.warn('Failed to parse media/variant map', error);
          return [];
        }
      }

      getCurrentSelectedOptions(variantSelects) {
        const selected = [];
        const fieldsets = Array.from(variantSelects.querySelectorAll('fieldset'));
        fieldsets.forEach((fieldset) => {
          const checked = fieldset.querySelector('input:checked');
          if (checked) selected.push(checked.value);
        });

        const selects = Array.from(variantSelects.querySelectorAll('select'));
        selects.forEach((select) => {
          if (select.value) selected.push(select.value);
        });

        return selected;
      }

      getBestVariantForMedia(mediaId, currentOptions) {
        const candidates = this.mediaVariantMap.filter((entry) => entry.mediaId === mediaId && Array.isArray(entry.options));
        if (!candidates.length) return null;
        if (candidates.length === 1) return candidates[0];

        let best = candidates[0];
        let bestScore = -1;
        candidates.forEach((candidate) => {
          let score = 0;
          candidate.options.forEach((value, index) => {
            if (currentOptions[index] && currentOptions[index] === value) score += 1;
          });
          if (score > bestScore) {
            best = candidate;
            bestScore = score;
          }
        });

        return best;
      }

      applyVariantOptions(variantSelects, targetOptions) {
        const fieldsets = Array.from(variantSelects.querySelectorAll('fieldset'));
        fieldsets.forEach((fieldset, index) => {
          const targetValue = targetOptions[index];
          if (!targetValue) return;
          const optionInput = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find(
            (input) => input.value === targetValue
          );
          if (!optionInput || optionInput.checked) return;
          optionInput.checked = true;
          optionInput.dispatchEvent(new Event('change', { bubbles: true }));
        });

        const selects = Array.from(variantSelects.querySelectorAll('select'));
        selects.forEach((select, index) => {
          const targetValue = targetOptions[index];
          if (!targetValue || select.value === targetValue) return;
          select.value = targetValue;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      syncVariantSelectionFromMedia(mediaId) {
        if (!mediaId || !this.mediaVariantMap.length) return;

        const productInfo = this.closest('product-info') || document.querySelector('product-info');
        const variantSelects = productInfo?.querySelector('variant-selects');
        if (!variantSelects) return;

        const currentOptions = this.getCurrentSelectedOptions(variantSelects);
        const bestVariant = this.getBestVariantForMedia(mediaId, currentOptions);
        if (!bestVariant) return;

        this.applyVariantOptions(variantSelects, bestVariant.options);
      }
    }
  );
}
